import React, { useState } from 'react';
import { FileText, Search, MapPin, User } from 'lucide-react';
import DataTable from 'components/common/DataTable';
import Pagination from 'components/Pagination';
import SearchableFilterSelect from 'components/common/SearchableFilterSelect';

const DetalhamentoOSSTable = ({ data = [], loading = false }) => {
  const [busca, setBusca] = useState('');
  const [filtroCidade, setFiltroCidade] = useState('');
  const [filtroTecnico, setFiltroTecnico] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Garantir que data seja sempre um array (última camada de proteção)
  const dadosArray = Array.isArray(data) ? data : [];
  
  // Se não for array, logar para debug
  if (!Array.isArray(data)) {
    console.warn('DetalhamentoOSSTable recebeu dados não-array:', {
      tipo: typeof data,
      valor: data,
      stack: new Error().stack
    });
  }

  // Extrair listas únicas para filtros
  const cidades = [...new Set(dadosArray.map(item => item.cidade).filter(Boolean))].sort();
  const tecnicos = [...new Set(dadosArray.map(item => item.tecnico).filter(Boolean))].sort();

  // Filtrar dados baseado nos filtros
  const dadosFiltrados = dadosArray.filter(item => {
    const matchBusca = 
      item.os_id?.toString().includes(busca.toLowerCase()) ||
      (item.cliente && String(item.cliente).toLowerCase().includes(busca.toLowerCase())) ||
      (item.produto && String(item.produto).toLowerCase().includes(busca.toLowerCase())) ||
      (item.assunto && String(item.assunto).toLowerCase().includes(busca.toLowerCase()));
    
    const matchCidade = !filtroCidade || item.cidade === filtroCidade;
    const matchTecnico = !filtroTecnico || item.tecnico === filtroTecnico;
    
    return matchBusca && matchCidade && matchTecnico;
  });

  // Resetar página quando filtros mudarem
  React.useEffect(() => {
    setPage(1);
  }, [busca, filtroCidade, filtroTecnico]);

  // Paginação
  const totalPages = Math.ceil(dadosFiltrados.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const dadosPaginados = dadosFiltrados.slice(startIndex, startIndex + itemsPerPage);

  // Funções específicas para configuração desta tabela
  const getDetalhamentoColumnStyle = (columnKey) => {
    if (columnKey.includes('QTD') || columnKey.includes('QUANTIDADE')) {
      return { minWidth: '50px', width: '4%' };
    }
    if (columnKey.includes('ID')) {
      return { minWidth: '80px', width: '6%' };
    }
    if (columnKey.includes('DATA')) {
      return { minWidth: '90px', width: '6%' };
    }
    if (columnKey.includes('CIDADE')) {
      return { minWidth: '100px', width: '8%' };
    }
    if (columnKey.includes('UNIDADE')) {
      return { minWidth: '80px', width: '6%' };
    }
    if (columnKey.includes('CUSTO') || columnKey.includes('VALOR')) {
      return { minWidth: '100px', width: '8%' };
    }
    if (columnKey.includes('TECNICO') || columnKey.includes('FUNCIONARIO')) {
      return { minWidth: '120px', width: '10%' };
    }
    if (columnKey.includes('CLIENTE')) {
      return { minWidth: '150px', width: '15%' };
    }
    if (columnKey.includes('PRODUTO')) {
      return { minWidth: '180px', width: '18%' };
    }
    if (columnKey.includes('SUBGRUPO')) {
      return { minWidth: '180px', width: '18%' };
    }
    return { minWidth: '100px', width: '10%' };
  };

  const getDetalhamentoPadding = (columnKey) => {
    if (columnKey.includes('QTD') || columnKey.includes('QUANTIDADE') || columnKey.includes('ID')) {
      return 'px-2 py-1';
    }
    if (columnKey.includes('DATA') || columnKey.includes('UNIDADE') || columnKey.includes('CUSTO') || columnKey.includes('VALOR')) {
      return 'px-2 py-2';
    }
    return 'px-3 py-2';
  };

  const formatarDataBR = (value) => {
    if (!value) return 'N/A';

    const texto = String(value).trim();
    const matchIso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (matchIso) {
      const [, ano, mes, dia] = matchIso;
      return `${dia}/${mes}/${ano}`;
    }

    const data = new Date(texto);
    if (!Number.isNaN(data.getTime())) {
      return data.toLocaleDateString('pt-BR');
    }

    return texto;
  };

  const columns = [
    {
      key: 'os_id',
      label: 'ID OS',
      sortable: true,
      className: 'text-left',
      render: (value) => (
        <span className="font-medium text-[color:var(--accent)] text-xs">
          {value?.toLocaleString('pt-BR') || 'N/A'}
        </span>
      )
    },
    {
      key: 'cliente',
      label: 'Cliente',
      sortable: true,
      className: 'text-left',
      cellClassName: 'px-3 py-2',
      render: (value) => (
        <span className="font-medium text-[color:var(--text-primary)] text-xs break-words" title={value}>
          {value || 'N/A'}
        </span>
      )
    },
    {
      key: 'cidade',
      label: 'Cidade',
      sortable: true,
      className: 'text-left',
      render: (value) => (
        <span className="text-[color:var(--text-muted)] text-xs" title={value}>
          {value || 'N/A'}
        </span>
      )
    },
    {
      key: 'produto',
      label: 'Produto',
      sortable: true,
      className: 'text-left',
      cellClassName: 'px-3 py-2',
      render: (value) => (
        <span className="font-medium text-[color:var(--text-primary)] text-xs break-words" title={value}>
          {value || 'N/A'}
        </span>
      )
    },
    {
      key: 'subgrupo',
      label: 'Subgrupo',
      sortable: true,
      className: 'text-left',
      cellClassName: 'px-3 py-2',
      render: (value) => (
        <span className="text-xs text-[color:var(--text-muted)] break-words" title={value}>
          {value || 'N/A'}
        </span>
      )
    },
    {
      key: 'quantidade',
      label: 'Qtd',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-medium text-[color:var(--accent)] text-xs">
          {Number(value || 0).toLocaleString('pt-BR', { 
            minimumFractionDigits: 0, 
            maximumFractionDigits: 2 
          })}
        </span>
      )
    },
    {
      key: 'unidade',
      label: 'Unidade',
      sortable: true,
      className: 'text-left',
      render: (value) => (
        <span className="text-xs text-[color:var(--text-muted)]">
          {value || 'N/A'}
        </span>
      )
    },
    {
      key: 'custo',
      label: 'Custo',
      sortable: true,
      className: 'text-right',
      render: (value) => (
        <span className="font-bold text-[color:var(--status-good)] text-xs">
          R$ {Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </span>
      )
    },
    {
      key: 'tecnico',
      label: 'Técnico',
      sortable: true,
      className: 'text-left',
      render: (value) => (
        <span className="text-xs text-[color:var(--text-muted)]" title={value}>
          {value || 'N/A'}
        </span>
      )
    },
    {
      key: 'data',
      label: 'Data',
      sortable: true,
      className: 'text-left',
      render: (value) => (
        <span className="text-xs text-[color:var(--text-muted)]">
          {formatarDataBR(value)}
        </span>
      )
    }
  ];

  return (
    <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[color:var(--border)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <FileText className="w-5 h-5 text-[color:var(--status-good)] mr-2" />
            <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
              Detalhamento OSS - Produtos Utilizados
              <span className="ml-2 text-sm text-[color:var(--text-muted)]">
                ({dadosFiltrados.length} de {dadosArray.length} itens)
              </span>
            </h3>
          </div>
          
          {/* Componente de Pesquisa Compacto */}
          <div className="flex items-end space-x-4">
            <div className="w-64">
              <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1 flex items-center gap-1">
                <Search className="h-3 w-3" />
                Buscar OSS
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-[color:var(--text-muted)]" />
                </div>
                <input
                  type="text"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar OS, cliente, produto ou assunto..."
                  className="block w-full pl-9 pr-3 py-2 text-sm border border-[color:var(--border)] rounded-md leading-5 bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                />
              </div>
            </div>
            
            <div className="w-48">
              <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Cidade
              </label>
              <SearchableFilterSelect
                options={cidades}
                value={filtroCidade}
                onChange={(value) => setFiltroCidade(value || '')}
                placeholder="Buscar cidade..."
                allOptionLabel="Todas as cidades"
                allOptionValue=""
                icon={MapPin}
              />
            </div>
            
            <div className="w-48">
              <label className="block text-xs font-medium text-[color:var(--text-muted)] mb-1 flex items-center gap-1">
                <User className="h-3 w-3" />
                Técnico
              </label>
              <SearchableFilterSelect
                options={tecnicos}
                value={filtroTecnico}
                onChange={(value) => setFiltroTecnico(value || '')}
                placeholder="Buscar técnico..."
                allOptionLabel="Todos os técnicos"
                allOptionValue=""
                icon={User}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="px-6 py-4">
        <DataTable
          data={dadosPaginados}
          columns={columns}
          loading={loading}
          maxHeight="max-h-96"
          emptyMessage="Nenhum detalhamento encontrado"
          customColumnWidths={getDetalhamentoColumnStyle}
          customColumnPadding={getDetalhamentoPadding}
          customFontSize="text-xs"
        />
      </div>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-[color:var(--border)]">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            totalItems={dadosFiltrados.length}
          />
        </div>
      )}

      {/* Resumo */}
      {dadosFiltrados.length > 0 && (
        <div className="px-6 py-4 bg-[color:var(--surface-muted)] border-t border-[color:var(--border)]">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-sm font-medium text-[color:var(--text-muted)]">Total de OSS</p>
              <p className="text-lg font-semibold text-[color:var(--text-primary)]">
                {dadosFiltrados.length}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-[color:var(--text-muted)]">Custo Total</p>
              <p className="text-lg font-semibold text-[color:var(--status-good)]">
                R$ {dadosFiltrados.reduce((total, item) => total + (Number(item.custo) || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-[color:var(--text-muted)]">Quantidade Total</p>
              <p className="text-lg font-semibold text-[color:var(--accent)]">
                {dadosFiltrados.reduce((total, item) => total + (Number(item.quantidade) || 0), 0).toLocaleString('pt-BR', { 
                  minimumFractionDigits: 0, 
                  maximumFractionDigits: 2 
                })}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-[color:var(--text-muted)]">Cidades Únicas</p>
              <p className="text-lg font-semibold text-[color:var(--accent)]">
                {new Set(dadosFiltrados.map(item => item.cidade).filter(Boolean)).size}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetalhamentoOSSTable;



